import React from 'react';

import cn from 'classnames';

import bem from 'client/services/bem';

import {ErrorMessageType} from 'client/common/inputs/error-message';
import InputWrapper from 'client/common/inputs/input-wrapper';

import {Translation} from 'client/models/language/types';

import {getWidth} from './helpers';

import cssModule from './textarea-input.module.scss';

const b = bem('textarea-input', {cssModule});

type Props = {
  classNames?: {
    value?: string;
    label?: string;
    input?: string;
    control?: string;
  };
  /** @deprecated Use classNames.input */
  textareaClassName?: string;
  /** @deprecated Use classNames.value */
  valueClassName?: string;
  label?: Translation;
  placeholder?: Translation;
  errorMessage?: ErrorMessageType;
  warningMessage?: Translation;
  readOnly?: boolean;
  width?: number | string;
  height?: number;
  isLinesLimited?: boolean;
  inputRef?: React.Ref<HTMLTextAreaElement>;
  required?: boolean;
  value?: string;
} & Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'placeholder' | 'readOnly' | 'ref' | 'value'>;

const TextareaInput: React.FC<Props> = (props) => {
  const {
    className,
    classNames = {},
    textareaClassName,
    valueClassName,
    label,
    name,
    disabled,
    placeholder,
    onChange,
    errorMessage,
    warningMessage,
    readOnly,
    width = 0,
    height = 0,
    isLinesLimited,
    inputRef,
    required,
    ...textareaProps
  } = props;

  const rootClassName = cn(b({error: !!errorMessage, warning: !!warningMessage}), className);
  const widthStyle = getWidth(width);
  const heightStyle = height && {
    minHeight: height && `${height}px`,
    maxHeight: height && `${height}px`,
  };

  const styles = {
    ...widthStyle,
    ...heightStyle,
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const textarea = event.target as HTMLTextAreaElement;
    const rows = textarea.rows || 1;
    const lines = textarea.value.split(/\r\n|\r|\n/).length;
    textarea.removeAttribute('maxLength');

    if (lines === rows && event.key === 'Enter' && isLinesLimited) {
      textarea.setAttribute('maxLength', textarea.value.length.toString());
      textarea.style.whiteSpace = 'pre';
    }
  };

  const handleMessageChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange?.(event);
  };

  const wrapperProps = {
    label,
    className: rootClassName,
    name,
    errorMessage,
    warningMessage,
    required,
    readOnly,
    value: textareaProps.value,
    classNames: {
      value: cn(valueClassName, classNames?.value, b('value')),
      content: cn(b('control'), classNames?.control),
      label: classNames?.label,
    },
  };

  return (
    <InputWrapper {...wrapperProps}>
      <textarea
        id={name}
        ref={inputRef}
        name={name}
        className={cn(b('component'), textareaClassName, classNames?.input)}
        disabled={disabled}
        placeholder={placeholder?.toString()}
        onChange={handleMessageChange}
        onKeyDown={handleKeyDown}
        style={styles}
        aria-invalid={!!errorMessage}
        {...textareaProps}
      />
    </InputWrapper>
  );
};

export default TextareaInput;
